import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

/**
 * Carte d'activation Stripe Connect Express pour le transporteur.
 * - Récupère le statut du compte connecté
 * - Permet de lancer/reprendre l'onboarding Stripe hébergé
 * - Permet d'accéder au dashboard Stripe Express une fois actif
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      // Erreur silencieuse — Stripe peut être temporairement indispo
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Si l'utilisateur revient depuis Stripe Onboarding, recharge auto
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_onboard")) {
      // Nettoie l'URL et recharge le statut
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 1000);
    }
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/connect/onboard", {
        origin_url: window.location.origin,
      });
      if (data.url) {
        // Ouvre Stripe dans un NOUVEL ONGLET pour échapper aux iframes (preview Emergent, etc.)
        // Stripe refuse de s'afficher en iframe pour des raisons de sécurité.
        const w = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!w) {
          // Bloqueur de pop-up actif → fallback : redirection complète au top de la fenêtre
          if (window.top) {
            window.top.location.href = data.url;
          } else {
            window.location.href = data.url;
          }
        } else {
          toast.success("Stripe ouvert dans un nouvel onglet. Revenez ici une fois terminé.");
          setBusy(false);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/connect/dashboard");
      if (data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const isActive = status?.payouts_enabled && status?.transfers_enabled;
  const inProgress = status?.has_account && !isActive;
  const notStarted = !status?.has_account;

  return (
    <div className="border border-[#1F4032]/10 p-6 mt-4" data-testid="stripe-connect-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <CreditCard className="w-6 h-6 text-[#1F4032] shrink-0 mt-1" />
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2 flex-wrap">
              Paiements Stripe Connect
              {isActive && (
                <span className="text-[10px] font-mono px-2 py-0.5 bg-emerald-600 text-white rounded flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> ACTIF
                </span>
              )}
              {inProgress && (
                <span className="text-[10px] font-mono px-2 py-0.5 bg-amber-500 text-white rounded flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> À COMPLÉTER
                </span>
              )}
              {notStarted && (
                <span className="text-[10px] font-mono px-2 py-0.5 bg-red-600 text-white rounded">
                  NON ACTIVÉ
                </span>
              )}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {isActive && "MML déclenche le reversement de 85 % après preuve de remise et confirmation de la livraison, hors incident ou revue en cours."}
              {inProgress && "Stripe a besoin de quelques informations supplémentaires (identité, RIB) pour activer vos paiements."}
              {notStarted && "Activez Stripe Connect pour recevoir les reversements (85 % du prix total) après validation de la livraison."}
            </p>
            {inProgress && (status?.requirements_currently_due || []).length > 0 && (
              <div className="text-xs text-amber-700 mt-2 font-mono break-words">
                Informations manquantes : {status.requirements_currently_due.join(", ")}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {(notStarted || inProgress) && (
            <Button
              onClick={startOnboarding}
              disabled={busy}
              className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2 w-full sm:w-auto"
              data-testid="stripe-onboard-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</>
                : <>{notStarted ? "Activer mes paiements" : "Compléter mon profil Stripe"}<ExternalLink className="w-4 h-4" /></>}
            </Button>
          )}
          {isActive && (
            <Button
              variant="outline"
              onClick={openDashboard}
              disabled={busy}
              className="rounded-full border-[#1F4032] gap-2"
              data-testid="stripe-dashboard-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Ouverture…</>
                : <>Voir mes paiements <ExternalLink className="w-4 h-4" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
